import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Icon } from '@iconify/react'
import { useNavigate, type NavigateFunction } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { useInstitution } from '@/contexts/InstitutionContext'
import { duplicateCampaign, fetchCampaigns, updateCampaign, type Campaign, type CampaignStatus } from '@/lib/campaigns'
import { canManageInstitutionCampaigns } from '@/lib/campaign-totp-step-up'
import { canListInstitutionCampaigns, canViewCampaignDashboard } from '@/lib/collaborator-permissions'
import { useCampaignTotpStepUp } from '@/contexts/CampaignTotpStepUpContext'
import { buildCampaignLandingEmbedSnippet } from '@/lib/campaignLandingEmbedSnippet'
import {
  CampaignSiteInstallModal,
  type CampaignSiteInstallCopyState,
} from '@/pages/CampanhasPage/CampaignSiteInstallModal'
import styles from './CampanhasPage.module.css'

type CampaignInstallModalState = {
  campaign: Campaign
  snippet: string
  copyState: CampaignSiteInstallCopyState
}

const cp = (n: number) => String.fromCodePoint(n)
const T = {
  titulo: `Gest${cp(0xe3)}o de campanhas promocionais`,
  subtitulo: `Visualize, acompanhe e organize seus sorteios com uma vis${cp(0xe3)}o clara de status e desempenho.`,
  erroLista: `N${cp(0xe3)}o foi poss${cp(0xed)}vel carregar as campanhas.`,
  erroDetalhes: `N${cp(0xe3)}o foi poss${cp(0xed)}vel carregar os detalhes da campanha.`,
  inicio: `In${cp(0xed)}cio`,
  preco: `Pre${cp(0xe7)}o`,
  acoes: `A${cp(0xe7)}${cp(0xf5)}es`,
  totalNumeros: `Total de n${cp(0xfa)}meros`,
  selecioneInstituicao: `Selecione uma institui${cp(0xe7)}${cp(0xe3)}o no menu acima para ver as campanhas.`,
}

const statusLabels: Record<CampaignStatus, string> = {
  draft: 'Rascunho',
  active: 'Ativa',
  finished: 'Finalizada',
  cancelled: 'Cancelada',
}

const statusIcons: Record<CampaignStatus, string> = {
  draft: 'mdi:pencil-outline',
  active: 'mdi:check-circle-outline',
  finished: 'mdi:flag-checkered',
  cancelled: 'mdi:close-circle-outline',
}

function formatDate(value?: string | null): string {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '-'
  return date.toLocaleDateString('pt-BR')
}

function formatCurrency(value?: number | string | null): string {
  if (value === null || value === undefined) return '-'
  const numValue = typeof value === 'string' ? parseFloat(value) : value
  if (isNaN(numValue)) return '-'
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(numValue)
}

function getStatusLabel(status?: CampaignStatus | null): string {
  if (!status) return 'N/D'
  return statusLabels[status] || 'N/D'
}

function getStatusIcon(status?: CampaignStatus | null): string {
  if (!status) return 'mdi:help-circle-outline'
  return statusIcons[status] || 'mdi:help-circle-outline'
}

function getStatusClass(status?: CampaignStatus | null): string {
  if (!status) return ''
  const classes: Record<CampaignStatus, string> = {
    draft: styles.statusDraft,
    active: styles.statusActive,
    finished: styles.statusFinished,
    cancelled: styles.statusCancelled,
  }
  return classes[status] || ''
}

type CampaignDashboardCardProps = {
  campaign: Campaign
  listKind: 'main' | 'hidden'
  canManage: boolean
  canViewDashboard: boolean
  navigate: NavigateFunction
  toggleHiddenBusyId: number | null
  duplicateBusyId: number | null
  onDuplicate: (campaignId: number) => void
  onSetHiddenFromMain: (campaignId: number, hidden: boolean) => void
  onInstallOnSite: (campaign: Campaign) => void
}

function CampaignDashboardCard({
  campaign,
  listKind,
  canManage,
  canViewDashboard,
  navigate,
  toggleHiddenBusyId,
  duplicateBusyId,
  onDuplicate,
  onSetHiddenFromMain,
  onInstallOnSite,
}: CampaignDashboardCardProps) {
  return (
    <div className={styles.campaignCard}>
      <div className={styles.campaignCardHeader}>
        <div className={styles.campaignIcon}>
          <Icon icon="mdi:bullhorn" />
        </div>
        <span className={`${styles.campaignStatus} ${getStatusClass(campaign.status)}`}>
          <Icon icon={getStatusIcon(campaign.status)} />
          {getStatusLabel(campaign.status)}
        </span>
      </div>

      <div className={styles.campaignCardBody}>
        <h4 className={styles.campaignName}>{campaign.name}</h4>
        <span className={styles.campaignSlug}>{campaign.slug || 'sem identificador'}</span>
      </div>

      <div className={styles.campaignCardDetails}>
        <div className={styles.campaignMetaGrid}>
          <div className={styles.metaCard}>
            <span className={styles.metaLabel}>Inicio</span>
            <div className={styles.metaItem}>
              <Icon icon="mdi:calendar-start" />
              <strong>{formatDate(campaign.starts_at)}</strong>
            </div>
          </div>
          <div className={styles.metaCard}>
            <span className={styles.metaLabel}>Encerramento</span>
            <div className={styles.metaItem}>
              <Icon icon="mdi:calendar-end" />
              <strong>{formatDate(campaign.ends_at)}</strong>
            </div>
          </div>
        </div>

        <div className={styles.campaignCardFooter}>
          <span className={styles.priceLabel}>Preco por numero</span>
          <div className={styles.campaignPrice}>{formatCurrency(campaign.price)}</div>
        </div>
      </div>

      <div className={styles.campaignCardActions}>
        <div className={styles.campaignCardActionsPrimary}>
          {campaign.slug ? (
            <a
              className={`${styles.actionBtn} ${styles.actionBtnPrimary}`}
              href={`/campanha/${campaign.slug}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              title="Abrir página da campanha em nova aba"
            >
              <Icon icon="mdi:eye" aria-hidden />
              Visualizar campanha
            </a>
          ) : (
            <button
              className={`${styles.actionBtn} ${styles.actionBtnPrimary}`}
              type="button"
              disabled
              title="Configure o slug na edição para visualizar"
            >
              <Icon icon="mdi:eye" aria-hidden />
              Visualizar campanha
            </button>
          )}
          {canViewDashboard ? (
          <button
            className={`${styles.actionBtn} ${styles.actionBtnAccent}`}
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              navigate(`/campanhas/dashboard?id=${campaign.id}`)
            }}
          >
            <Icon icon="mdi:chart-box-outline" aria-hidden />
            Dashboard
          </button>
          ) : null}
        </div>

        {canManage ? (
          <>
            <div className={styles.campaignCardActionsRow}>
              <button
                className={styles.actionBtn}
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  navigate(`/campanhas/editar?id=${campaign.id}`)
                }}
              >
                <Icon icon="mdi:pencil" aria-hidden />
                Editar
              </button>
              {listKind === 'main' ? (
                <button
                  className={styles.actionBtn}
                  type="button"
                  disabled={toggleHiddenBusyId === campaign.id}
                  onClick={(e) => {
                    e.stopPropagation()
                    void onSetHiddenFromMain(campaign.id, true)
                  }}
                  title="Remove dos seletores de campanha e desta listagem principal"
                >
                  <Icon icon="mdi:eye-off-outline" aria-hidden />
                  Ocultar
                </button>
              ) : (
                <button
                  className={styles.actionBtn}
                  type="button"
                  disabled={toggleHiddenBusyId === campaign.id}
                  onClick={(e) => {
                    e.stopPropagation()
                    void onSetHiddenFromMain(campaign.id, false)
                  }}
                  title="Volta a aparecer nos seletores e na listagem principal"
                >
                  <Icon icon="mdi:eye-outline" aria-hidden />
                  Restaurar
                </button>
              )}
            </div>

            <button
              className={styles.actionBtn}
              type="button"
              disabled={duplicateBusyId !== null}
              aria-busy={duplicateBusyId === campaign.id}
              onClick={(e) => {
                e.stopPropagation()
                onDuplicate(campaign.id)
              }}
              title="Criar uma cópia da campanha como rascunho"
            >
              <Icon icon="mdi:content-copy" aria-hidden />
              {duplicateBusyId === campaign.id ? 'Duplicando...' : 'Duplicar campanha'}
            </button>

            <button
              className={`${styles.actionBtn} ${styles.actionBtnMuted}`}
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                onInstallOnSite(campaign)
              }}
              title="Copiar script para incorporar a campanha em outro site"
            >
              <Icon icon="mdi:web-plus" aria-hidden />
              Instalar no meu site
            </button>
          </>
        ) : null}
      </div>
    </div>
  )
}

export function CampanhasTab() {
  const { guard, token, isCollaborator, hasPermission } = useAuth()
  const { institutionId } = useInstitution()
  const navigate = useNavigate()
  const { ensureElevated, runWithStepUp } = useCampaignTotpStepUp()
  const canManage = canManageInstitutionCampaigns(guard, Boolean(isCollaborator))
  const canViewDashboard = canViewCampaignDashboard(Boolean(isCollaborator), hasPermission)
  const canListCampaigns = canListInstitutionCampaigns(Boolean(isCollaborator), hasPermission)

  const [campaigns, setCampaigns] = useState<Campaign[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showHiddenPanel, setShowHiddenPanel] = useState(false)
  const [toggleHiddenBusyId, setToggleHiddenBusyId] = useState<number | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [duplicateBusyId, setDuplicateBusyId] = useState<number | null>(null)
  const duplicateInFlight = useRef(false)
  const [installModal, setInstallModal] = useState<CampaignInstallModalState | null>(null)

  const handleInstallOnSite = useCallback(async (campaign: Campaign) => {
    if (!campaign.id) return

    let snippet: string
    try {
      snippet = buildCampaignLandingEmbedSnippet({ campaignId: campaign.id })
    } catch {
      setErrorMessage(
        `N${cp(0xe3)}o foi poss${cp(0xed)}vel gerar o c${cp(0xf3)}digo de instala${cp(0xe7)}${cp(0xe3)}o. Configure VITE_PUBLIC_APP_URL ou abra o painel no dom${cp(0xed)}nio p${cp(0xfa)}blico do app.`,
      )
      return
    }

    let copyState: CampaignSiteInstallCopyState = 'copied'
    try {
      await navigator.clipboard.writeText(snippet)
    } catch {
      copyState = 'error'
    }

    setInstallModal({ campaign, snippet, copyState })
  }, [])

  const loadCampaigns = useCallback(async () => {
    setIsLoading(true)
    setErrorMessage(null)
    if (guard === 'admin' && institutionId === null) {
      setCampaigns([])
      setIsLoading(false)
      return
    }
    if (!canListCampaigns) {
      setCampaigns([])
      setIsLoading(false)
      return
    }
    try {
      const result = await fetchCampaigns(token ?? undefined)
      setCampaigns(result)
    } catch {
      setErrorMessage(T.erroLista)
    } finally {
      setIsLoading(false)
    }
  }, [canListCampaigns, token, institutionId, guard])

  useEffect(() => {
    loadCampaigns()
  }, [loadCampaigns])

  const mainCampaigns = useMemo(
    () => campaigns.filter((c) => !c.hidden_from_main),
    [campaigns]
  )
  const hiddenCampaigns = useMemo(
    () => campaigns.filter((c) => Boolean(c.hidden_from_main)),
    [campaigns]
  )

  const listStats = useMemo(() => {
    const total = campaigns.length
    const active = campaigns.filter((campaign) => campaign.status === 'active').length
    const draft = campaigns.filter((campaign) => campaign.status === 'draft').length
    const finished = campaigns.filter((campaign) => campaign.status === 'finished').length
    const cancelled = campaigns.filter((campaign) => campaign.status === 'cancelled').length
    return { total, active, draft, finished, cancelled }
  }, [campaigns])

  const handleSetHiddenFromMain = useCallback(
    async (campaignId: number, hidden: boolean) => {
      if (!token || !canManage) return
      const allowed = await ensureElevated()
      if (!allowed) return
      setToggleHiddenBusyId(campaignId)
      setErrorMessage(null)
      try {
        await runWithStepUp(() => updateCampaign(token, campaignId, { hidden_from_main: hidden }))
        await loadCampaigns()
      } catch {
        setErrorMessage(
          hidden
            ? `N${cp(0xe3)}o foi poss${cp(0xed)}vel ocultar a campanha.`
            : `N${cp(0xe3)}o foi poss${cp(0xed)}vel restaurar a campanha na lista principal.`
        )
      } finally {
        setToggleHiddenBusyId(null)
      }
    },
    [canManage, ensureElevated, loadCampaigns, runWithStepUp, token]
  )

  const handleDuplicate = useCallback(async (campaignId: number) => {
    if (!token || !canManage || duplicateInFlight.current) return
    duplicateInFlight.current = true
    setDuplicateBusyId(campaignId)
    setErrorMessage(null)
    setSuccessMessage(null)
    try {
      if (!(await ensureElevated())) return
      const copy = await runWithStepUp(() => duplicateCampaign(token, campaignId))
      setCampaigns((current) => [copy, ...current])
      if (copy.hidden_from_main) setShowHiddenPanel(true)
      setSuccessMessage(`“${copy.name}” foi criada como rascunho. Você já pode editar a cópia.`)
    } catch {
      setErrorMessage('Não foi possível confirmar a duplicação. Atualize a lista antes de tentar novamente.')
    } finally {
      duplicateInFlight.current = false
      setDuplicateBusyId(null)
    }
  }, [canManage, ensureElevated, runWithStepUp, token])

  return (
    <div className={styles.page}>
      <div className={styles.heroCard}>
        <div className={styles.heroText}>
          <span className={styles.kicker}>Campanhas</span>
          <h2 className={styles.heroTitle}>{T.titulo}</h2>
          <p className={styles.heroSubtitle}>{T.subtitulo}</p>
          <div className={styles.statsRow}>
            <div className={styles.statItem}>
              <div className={styles.statIcon}>
                <Icon icon="mdi:folder-multiple" />
              </div>
              <div className={styles.statContent}>
                <span>Total</span>
                <strong>{listStats.total}</strong>
              </div>
            </div>
            <div className={styles.statItem}>
              <div className={`${styles.statIcon} ${styles.statIconActive}`}>
                <Icon icon="mdi:play-circle" />
              </div>
              <div className={styles.statContent}>
                <span>Ativas</span>
                <strong>{listStats.active}</strong>
              </div>
            </div>
            <div className={styles.statItem}>
              <div className={`${styles.statIcon} ${styles.statIconDraft}`}>
                <Icon icon="mdi:pencil" />
              </div>
              <div className={styles.statContent}>
                <span>Rascunhos</span>
                <strong>{listStats.draft}</strong>
              </div>
            </div>
            <div className={styles.statItem}>
              <div className={`${styles.statIcon} ${styles.statIconFinished}`}>
                <Icon icon="mdi:check-all" />
              </div>
              <div className={styles.statContent}>
                <span>Finalizadas</span>
                <strong>{listStats.finished}</strong>
              </div>
            </div>
            <div className={styles.statItem}>
              <div className={`${styles.statIcon} ${styles.statIconCancelled}`}>
                <Icon icon="mdi:close-circle" />
              </div>
              <div className={styles.statContent}>
                <span>Canceladas</span>
                <strong>{listStats.cancelled}</strong>
              </div>
            </div>
          </div>
        </div>
      </div>

      {errorMessage && (
        <div className={styles.errorBanner} role="alert">
          <Icon icon="mdi:alert-circle" />
          {errorMessage}
        </div>
      )}

      {successMessage && <div className={styles.successBanner} role="status">{successMessage}</div>}

      <div className={styles.contentStack}>
        <section className={styles.panelCard}>
          <div className={styles.panelHeader}>
            <div>
              <h3 className={styles.sectionTitle}>Campanhas cadastradas</h3>
              <p className={styles.sectionSubtitle}>
                Lista principal usada nos seletores do sistema. Campanhas ocultas ficam fora desta lista.
              </p>
            </div>
            {!isLoading &&
            !(guard === 'admin' && institutionId === null) &&
            campaigns.length > 0 &&
            hiddenCampaigns.length > 0 ? (
              <button
                type="button"
                className={styles.ghostButton}
                onClick={() => setShowHiddenPanel((previous) => !previous)}
              >
                {showHiddenPanel
                  ? 'Fechar campanhas ocultas'
                  : `Ver campanhas ocultas (${hiddenCampaigns.length})`}
              </button>
            ) : null}
          </div>

          {isLoading ? (
            <div className={styles.loadingState}>
              <div className={styles.loadingSpinner} />
              <span>Carregando campanhas...</span>
            </div>
          ) : guard === 'admin' && institutionId === null ? (
            <div className={styles.emptyState}>
              <div className={styles.emptyIcon}>
                <Icon icon="mdi:office-building-outline" />
              </div>
              <h4>Selecione uma instituição</h4>
              <p>{T.selecioneInstituicao}</p>
            </div>
          ) : campaigns.length === 0 ? (
            <div className={styles.emptyState}>
              <div className={styles.emptyIcon}>
                <Icon icon="mdi:bullhorn-outline" />
              </div>
              <h4>Nenhuma campanha cadastrada</h4>
              <p>Comece criando sua primeira campanha promocional</p>
            </div>
          ) : mainCampaigns.length === 0 ? (
            <div className={styles.emptyState}>
              <div className={styles.emptyIcon}>
                <Icon icon="mdi:eye-off-outline" />
              </div>
              <h4>Nenhuma campanha na lista principal</h4>
              <p>
                Todas as campanhas estão ocultas dos seletores e desta visão. Use o botão acima para ver as
                ocultas e restaurar as que quiser.
              </p>
            </div>
          ) : (
            <div className={styles.campaignGrid}>
              {mainCampaigns.map((campaign) => (
                <CampaignDashboardCard
                  key={campaign.id}
                  campaign={campaign}
                  listKind="main"
                  canManage={canManage}
                  canViewDashboard={canViewDashboard}
                  navigate={navigate}
                  toggleHiddenBusyId={toggleHiddenBusyId}
                  duplicateBusyId={duplicateBusyId}
                  onDuplicate={(id) => void handleDuplicate(id)}
                  onSetHiddenFromMain={handleSetHiddenFromMain}
                  onInstallOnSite={(selected) => void handleInstallOnSite(selected)}
                />
              ))}
            </div>
          )}
        </section>

        {!isLoading &&
        !(guard === 'admin' && institutionId === null) &&
        showHiddenPanel &&
        hiddenCampaigns.length > 0 ? (
          <section className={styles.panelCard}>
            <div className={styles.panelHeader}>
              <div>
                <h3 className={styles.sectionTitle}>Campanhas ocultas</h3>
                <p className={styles.sectionSubtitle}>
                  Estas campanhas não aparecem nos seletores nem na listagem principal até serem restauradas.
                </p>
              </div>
            </div>
            <div className={styles.campaignGrid}>
              {hiddenCampaigns.map((campaign) => (
                <CampaignDashboardCard
                  key={campaign.id}
                  campaign={campaign}
                  listKind="hidden"
                  canManage={canManage}
                  canViewDashboard={canViewDashboard}
                  navigate={navigate}
                  toggleHiddenBusyId={toggleHiddenBusyId}
                  duplicateBusyId={duplicateBusyId}
                  onDuplicate={(id) => void handleDuplicate(id)}
                  onSetHiddenFromMain={handleSetHiddenFromMain}
                  onInstallOnSite={(selected) => void handleInstallOnSite(selected)}
                />
              ))}
            </div>
          </section>
        ) : null}
      </div>

      {installModal ? (
        <CampaignSiteInstallModal
          campaignName={installModal.campaign.name}
          campaignId={installModal.campaign.id}
          slug={installModal.campaign.slug?.trim() || null}
          snippet={installModal.snippet}
          initialCopyState={installModal.copyState}
          onClose={() => setInstallModal(null)}
        />
      ) : null}
    </div>
  )
}
